// src/lib/pwa.ts: the installable app in the page (docs/DECISIONS.md, "Installable app").
//   isStandalone()  the installed app: display-mode standalone, or iOS's navigator.standalone, or fullscreen only
//                   where that property exists (iOS WebKit): desktop Chrome also says fullscreen for a plain tab in a
//                   full-screen window
//   markDisplay()   <html data-display="app|browser">, set before the first render; the installed app's safe-area CSS
//                   (src/styles/app.css) keys on "app", so the browser is untouched
//   startPwa(mode)  after boot, from GET /api/tenant's site.pwa. "off" is the kill switch: every service worker of this
//                   origin is unregistered and every cache deleted. Otherwise the manifest and touch-icon links go in
//                   once, /sw.js registers, Chrome's automatic install banner is held back (its event kept for the
//                   install card, src/pwa/prompt.ts), and the app checks for a new worker when it comes back after an
//                   hour away (a Home Screen app stays open for days).
import { signal, type ReadonlySignal } from "@preact/signals";
import { parsePwaMode, pwaAllows, type PwaMode } from "../../shared/pwa";
import type { InstallPromptEvent } from "../pwa/install";
import { RETIRE_MESSAGE } from "../sw/policy";

export const SW_URL = "/sw.js";
export const UPDATE_AFTER_HIDDEN_MS = 60 * 60_000;

type DisplayWindow = { matchMedia?: (query: string) => { matches: boolean }; navigator: { standalone?: boolean } };

export function isStandalone(win: DisplayWindow = window as unknown as DisplayWindow): boolean {
  const matches = (query: string) => {
    try {
      return !!win.matchMedia?.(query).matches;
    } catch {
      return false;
    }
  };
  const ios = "standalone" in win.navigator;
  return matches("(display-mode: standalone)") || win.navigator.standalone === true || (ios && matches("(display-mode: fullscreen)"));
}

export function markDisplay(doc: Document = document, win?: DisplayWindow): "app" | "browser" {
  const display = isStandalone(win) ? "app" : "browser";
  doc.documentElement.dataset.display = display;
  return display;
}

/** Whether this page runs as the installed app (markDisplay ran at startup). */
export function inApp(doc: Document = document): boolean {
  return doc.documentElement.dataset.display === "app";
}

type Registrations = Pick<ServiceWorkerContainer, "register" | "getRegistrations"> & { controller?: Pick<ServiceWorker, "postMessage"> | null };
export type PwaDeps = { doc?: Document; serviceWorker?: Registrations | null; caches?: CacheStorage | null; now?: () => number };

/**
 * The kill switch: unregisters every service worker of this origin and deletes every cache. A worker that still
 * controls this page (until it navigates) is told to stop caching first; and since it writes only into a cache that
 * exists (src/sw/sw.ts, store), it cannot bring one back after a restart either. Returns how many registrations it
 * removed.
 */
export async function removeServiceWorkers(serviceWorker: Registrations | null | undefined, cacheStorage: CacheStorage | null | undefined): Promise<number> {
  let removed = 0;
  try {
    serviceWorker?.controller?.postMessage({ type: RETIRE_MESSAGE });
  } catch {
    // Nothing controls the page, or the worker is already gone.
  }
  const regs = serviceWorker ? await serviceWorker.getRegistrations().catch(() => []) : [];
  for (const reg of regs) if (await reg.unregister().catch(() => false)) removed++;
  const keys = cacheStorage ? await cacheStorage.keys().catch(() => [] as string[]) : [];
  for (const key of keys) await cacheStorage!.delete(key).catch(() => false);
  return removed;
}

function linkOnce(doc: Document, rel: string, href: string): void {
  if (doc.head.querySelector(`link[rel="${rel}"]`)) return;
  const link = doc.createElement("link");
  link.rel = rel;
  link.href = href;
  doc.head.appendChild(link);
}

const watched = new WeakSet<Document>();

/** One registration.update() when the page is shown again after an hour hidden. */
function watchForUpdates(reg: ServiceWorkerRegistration, doc: Document, now: () => number): void {
  if (watched.has(doc)) return;
  watched.add(doc);
  let hiddenAt = 0;
  doc.addEventListener("visibilitychange", () => {
    if (doc.visibilityState === "hidden") hiddenAt = now();
    else if (hiddenAt && now() - hiddenAt > UPDATE_AFTER_HIDDEN_MS) {
      hiddenAt = 0;
      void reg.update().catch(() => undefined);
    }
  });
}

const quietWindows = new WeakSet<Window>();
const heldPrompt = signal<InstallPromptEvent | null>(null);

/** Chrome's install event, held back and kept (Android and desktop Chromium); null until Chrome offers one. */
export const heldInstallPrompt: ReadonlySignal<InstallPromptEvent | null> = heldPrompt;

/**
 * Takes the kept event, which can prompt() only once: the next one comes from Chrome (the next page load, and never
 * once the app is installed).
 */
export function takeInstallPrompt(): InstallPromptEvent | null {
  const e = heldPrompt.value;
  heldPrompt.value = null;
  return e;
}

/**
 * No automatic install banner: Chrome's mini-infobar would pop up on a family's first visit. The event is kept instead,
 * so the install card, the Settings row or /install can open Chrome's dialog in one tap at a better moment.
 */
function quietInstallPrompt(win: Window): void {
  if (quietWindows.has(win)) return;
  quietWindows.add(win);
  win.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    heldPrompt.value = e as InstallPromptEvent;
  });
  // Installed (through us or the browser's menu): nothing is left to offer.
  win.addEventListener("appinstalled", () => {
    heldPrompt.value = null;
  });
}

export async function startPwa(mode: PwaMode | undefined, deps: PwaDeps = {}): Promise<void> {
  const doc = deps.doc ?? document;
  const serviceWorker = deps.serviceWorker !== undefined ? deps.serviceWorker : "serviceWorker" in navigator ? navigator.serviceWorker : null;
  const cacheStorage = deps.caches !== undefined ? deps.caches : typeof caches === "undefined" ? null : caches;
  if (!pwaAllows(parsePwaMode(mode), "install")) {
    await removeServiceWorkers(serviceWorker, cacheStorage);
    return;
  }
  quietInstallPrompt(doc.defaultView ?? window);
  linkOnce(doc, "manifest", "/manifest.webmanifest");
  linkOnce(doc, "apple-touch-icon", "/apple-touch-icon.png");
  if (!serviceWorker) return;
  try {
    // updateViaCache "none": every update check asks the network for /sw.js, never the HTTP cache.
    const reg = await serviceWorker.register(SW_URL, { scope: "/", updateViaCache: "none" });
    watchForUpdates(reg, doc, deps.now ?? Date.now);
  } catch {
    // A browser that refuses (private mode, an old engine) keeps working as a plain page.
  }
}
